"use client";

import { useLingui } from "@lingui/react/macro";
import { FileQuestionIcon } from "@offstreetparking/design-system/icons/FileQuestionIcon";
import { WarningIcon } from "@offstreetparking/design-system/icons/WarningIcon";
import { captureException } from "@offstreetparking/instrumentation/client";
import type { TRPCClientErrorLike } from "@trpc/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useParker } from "../../../providers/parker";
import { routes } from "../../../routes";
import { GeneralErrorAlert } from "./GeneralErrorAlert";
class ServerError extends Error implements TRPCClientErrorLike<any> {
	readonly data: TRPCClientErrorLike<any>["data"];
	readonly shape: TRPCClientErrorLike<any>["shape"];
	constructor({ message, data, shape }: TRPCClientErrorLike<any>) {
		super(message);
		this.name = "TRPCClientErrorLike";
		this.data = data;
		this.shape = shape;
	}
}
type ServerErrorAlertProps = {
	serverError?: TRPCClientErrorLike<any> | null | undefined;
	title?: string | null;
};
export const ServerErrorAlert = ({ serverError, title }: ServerErrorAlertProps) => {
	const { t } = useLingui();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const queryParams = searchParams.toString();
	const router = useRouter();
	const { isLoggedIn } = useParker();
	const referenceId = serverError?.data?.referenceId;
	const httpStatus = serverError?.data?.httpStatus || serverError?.data?.code;
	useEffect(() => {
		// If there is a server error here without a code // http status, then
		// this is an unexpected error shape, capturing so we can investigate
		if (serverError && !httpStatus) {
			captureException(new ServerError(serverError), {
				extra: {
					httpStatus: "unexpected",
				},
			});
		}
	}, [serverError, httpStatus]);
	if (!serverError) {
		return null;
	}
	if (httpStatus === 500) {
		return (
			<GeneralErrorAlert
				icon={WarningIcon}
				title={title || t`Something went wrong`}
				message={[
					t`We couldn't load this page. Please try again, and if the issue`,
					t`continues, contact Support.`,
				]}
				referenceId={referenceId}
				showContactSupport={true}
			/>
		);
	}
	if (httpStatus === 404) {
		return (
			<GeneralErrorAlert
				icon={FileQuestionIcon}
				title={t`Page Not Found`}
				message={[
					t`The link you entered doesn't match any active permits or events.`,
					t`Please check for typos or try again.`,
				]}
				showContactSupport={false}
			/>
		);
	}
	if ((httpStatus === 401 || httpStatus === 403) && !isLoggedIn) {
		return (
			<GeneralErrorAlert
				title={t`Login Required`}
				message={[t`You are not logged in.`, t`Please log in to view this page.`]}
				showContactSupport={false}
				customAction={{
					label: t({
						message: "Login",
						comment:
							"One-word button label for the sign-in action, not a username field. Sends the user to the login page.",
					}),
					onClick: () =>
						router.push(
							routes.login({
								redirectTo: `${pathname}${queryParams ? `?${queryParams}` : ""}`,
							}),
						),
				}}
			/>
		);
	}
	if (httpStatus === 403 || httpStatus === 401) {
		return (
			<GeneralErrorAlert
				title={t`Unauthorized`}
				message={[t`You are not authorized to view this page.`]}
				showContactSupport={false}
			/>
		);
	}
	return <GeneralErrorAlert />;
};
